import {useCallback} from 'react';
import {NavigateOptions, useNavigate} from 'react-router-dom';

import {useActiveCasusIdOrThrow} from '_/hooks/useActiveCasusId';
import {Arguments, getRouteUrl} from '_/utilities';
import {Optional} from '_/utilities/types';

type ExtractModuleArguments<M extends Arguments['module']> = Omit<
  Extract<Arguments, {module: M}>,
  'module'
>;

type CasusLogViewParams = Optional<
  ExtractModuleArguments<'casus-logView'>,
  'casusId'
>;
type CasusDocumentsParams = Optional<
  ExtractModuleArguments<'casus-documents'>,
  'casusId'
>;
type CasusJustitiaSubmissionParams = {
  casusId?: string;
  submissionId: string;
  reloadSubmissionFolders?: boolean;
};
type CasusEmailsParams = Optional<
  ExtractModuleArguments<'casus-communication'>,
  'casusId'
>;
type CasusNotesParams = Optional<
  ExtractModuleArguments<'casus-notes'>,
  'casusId'
>;
type CasusTasksParams = Optional<
  ExtractModuleArguments<'casus-tasks'>,
  'casusId'
>;
type CasusFeesParams = Optional<
  ExtractModuleArguments<'casus-fees'>,
  'casusId'
>;
type CasusInvoiceContainersParams = Optional<
  ExtractModuleArguments<'casus-invoice-containers'>,
  'casusId'
>;
type CasusInvoicesParams = Optional<
  ExtractModuleArguments<'casus-invoices'>,
  'casusId'
>;
type CasusPaymentsParams = Optional<
  ExtractModuleArguments<'casus-payments'>,
  'casusId'
>;
type CasusClientMoneyParams = Optional<
  ExtractModuleArguments<'casus-client-money'>,
  'casusId'
>;

type ContactsParams = ExtractModuleArguments<'contacts'>;

type CommunicationsParams = Omit<
  ExtractModuleArguments<'communication'>,
  'view'
>;

type TasksParams = ExtractModuleArguments<'tasks'>;

type ExtractInvoicingViewArguments<
  V extends ExtractModuleArguments<'invoicing'>['view'],
> = Omit<Extract<Arguments, {module: 'invoicing'; view: V}>, 'module' | 'view'>;

type InvoiceContainersParams =
  ExtractInvoicingViewArguments<'invoice-containers'>;
type InvoicesParams = ExtractInvoicingViewArguments<'invoices'>;
type PaymentsParams = ExtractInvoicingViewArguments<'payments'>;
type ClientMoneyParams = ExtractInvoicingViewArguments<'client-money'>;
type FinanceExportParams = ExtractInvoicingViewArguments<'finance-export'>;

type SearchParams = ExtractModuleArguments<'search'>;

type ReturnType = {
  goToCasusList: () => void;
  goToCasusLogView: (params: CasusLogViewParams) => void;
  goToCasusDocuments: (params: CasusDocumentsParams) => void;
  goToCasusJustitiaSubmission: (params: CasusJustitiaSubmissionParams) => void;
  goToCasusEmails: (params: CasusEmailsParams) => void;
  goToCasusNotes: (params: CasusNotesParams) => void;
  goToCasusTasks: (
    params: CasusTasksParams,
    navigationOptions?: NavigateOptions
  ) => void;
  goToCasusFees: (params: CasusFeesParams) => void;
  goToCasusInvoiceContainers: (params: CasusInvoiceContainersParams) => void;
  goToCasusInvoices: (params: CasusInvoicesParams) => void;
  goToCasusPayments: (params: CasusPaymentsParams) => void;
  goToCasusClientMoney: (params: CasusClientMoneyParams) => void;
  goToContacts: (params: ContactsParams) => void;
  goToTriage: (params: CommunicationsParams) => void;
  goToAllEmails: (params: CommunicationsParams) => void;
  goToTasks: (params: TasksParams) => void;
  goToInvoiceContainers: (params: InvoiceContainersParams) => void;
  goToInvoices: (params: InvoicesParams) => void;
  goToPayments: (params: PaymentsParams) => void;
  goToClientMoney: (params: ClientMoneyParams) => void;
  goToFinanceExport: (params: FinanceExportParams) => void;
  goToSearch: (params: SearchParams) => void;
};

export const useRouteNavigate = (): ReturnType => {
  const navigate = useNavigate();
  const {getActiveCasusIdOrThrow} = useActiveCasusIdOrThrow();

  const goToCasusList = useCallback(() => {
    navigate(getRouteUrl({module: 'casus'}));
  }, [navigate]);

  const goToCasusLogView = useCallback(
    ({
      casusId = getActiveCasusIdOrThrow(),
      clearFilters = true,
      ...params
    }: CasusLogViewParams) => {
      navigate(
        getRouteUrl({module: 'casus-logView', casusId, clearFilters, ...params})
      );
    },
    [navigate, getActiveCasusIdOrThrow]
  );

  const goToCasusDocuments = useCallback(
    ({
      casusId = getActiveCasusIdOrThrow(),
      clearFilters = true,
      ...params
    }: CasusDocumentsParams) => {
      navigate(
        getRouteUrl({
          module: 'casus-documents',
          casusId,
          clearFilters,
          ...params,
        })
      );
    },
    [navigate, getActiveCasusIdOrThrow]
  );

  const goToCasusJustitiaSubmission = useCallback(
    ({
      submissionId,
      casusId = getActiveCasusIdOrThrow(),
      reloadSubmissionFolders,
    }: CasusJustitiaSubmissionParams) => {
      navigate(
        getRouteUrl({
          module: 'casus-documents-submissions',
          casusId,
          submissionId,
          reloadSubmissionFolders,
        })
      );
    },
    [navigate, getActiveCasusIdOrThrow]
  );

  const goToCasusEmails = useCallback(
    ({
      casusId = getActiveCasusIdOrThrow(),
      clearFilters = true,
      ...params
    }: CasusEmailsParams) => {
      navigate(
        getRouteUrl({
          module: 'casus-communication',
          casusId,
          clearFilters,
          ...params,
        })
      );
    },
    [navigate, getActiveCasusIdOrThrow]
  );

  const goToCasusNotes = useCallback(
    ({
      casusId = getActiveCasusIdOrThrow(),
      clearFilters = true,
      ...params
    }: CasusNotesParams) => {
      navigate(
        getRouteUrl({module: 'casus-notes', casusId, clearFilters, ...params})
      );
    },
    [navigate, getActiveCasusIdOrThrow]
  );

  const goToCasusTasks = useCallback(
    ({
      casusId = getActiveCasusIdOrThrow(),
      clearFilters = true,
      ...params
    }: CasusTasksParams) => {
      navigate(
        getRouteUrl({module: 'casus-tasks', casusId, clearFilters, ...params})
      );
    },
    [navigate, getActiveCasusIdOrThrow]
  );

  const goToCasusFees = useCallback(
    ({
      casusId = getActiveCasusIdOrThrow(),
      clearFilters = true,
      ...params
    }: CasusFeesParams) => {
      navigate(
        getRouteUrl({module: 'casus-fees', casusId, clearFilters, ...params})
      );
    },
    [navigate, getActiveCasusIdOrThrow]
  );

  const goToCasusInvoiceContainers = useCallback(
    ({
      casusId = getActiveCasusIdOrThrow(),
      clearFilters = true,
      ...params
    }: CasusInvoiceContainersParams) => {
      navigate(
        getRouteUrl({
          module: 'casus-invoice-containers',
          casusId,
          clearFilters,
          ...params,
        })
      );
    },
    [navigate, getActiveCasusIdOrThrow]
  );

  const goToCasusInvoices = useCallback(
    ({
      casusId = getActiveCasusIdOrThrow(),
      clearFilters = true,
      ...params
    }: CasusInvoicesParams) => {
      navigate(
        getRouteUrl({
          module: 'casus-invoices',
          casusId,
          clearFilters,
          ...params,
        })
      );
    },
    [navigate, getActiveCasusIdOrThrow]
  );

  const goToCasusPayments = useCallback(
    ({
      casusId = getActiveCasusIdOrThrow(),
      clearFilters = true,
      ...params
    }: CasusPaymentsParams) => {
      navigate(
        getRouteUrl({
          module: 'casus-payments',
          casusId,
          clearFilters,
          ...params,
        })
      );
    },
    [navigate, getActiveCasusIdOrThrow]
  );

  const goToCasusClientMoney = useCallback(
    ({
      casusId = getActiveCasusIdOrThrow(),
      clearFilters = true,
      ...params
    }: CasusClientMoneyParams) => {
      navigate(
        getRouteUrl({
          module: 'casus-client-money',
          casusId,
          clearFilters,
          ...params,
        })
      );
    },
    [navigate, getActiveCasusIdOrThrow]
  );

  const goToContacts = useCallback(
    ({clearFilters = true, ...params}: ContactsParams) => {
      navigate(getRouteUrl({module: 'contacts', clearFilters, ...params}));
    },
    [navigate]
  );

  const goToTriage = useCallback(
    ({clearFilters = true, ...params}: CommunicationsParams) => {
      navigate(
        getRouteUrl({
          module: 'communication',
          view: 'triage',
          clearFilters,
          ...params,
        })
      );
    },
    [navigate]
  );

  const goToAllEmails = useCallback(
    ({clearFilters = true, ...params}: CommunicationsParams) => {
      navigate(
        getRouteUrl({
          module: 'communication',
          view: 'all',
          clearFilters,
          ...params,
        })
      );
    },
    [navigate]
  );

  const goToTasks = useCallback(
    ({clearFilters = true, ...params}: TasksParams) => {
      navigate(getRouteUrl({module: 'tasks', clearFilters, ...params}));
    },
    [navigate]
  );

  const goToInvoiceContainers = useCallback(
    ({clearFilters = true, ...params}: InvoiceContainersParams) => {
      navigate(
        getRouteUrl({
          module: 'invoicing',
          view: 'invoice-containers',
          clearFilters,
          ...params,
        })
      );
    },
    [navigate]
  );

  const goToInvoices = useCallback(
    ({clearFilters = true, ...params}: InvoicesParams) => {
      navigate(
        getRouteUrl({
          module: 'invoicing',
          view: 'invoices',
          clearFilters,
          ...params,
        })
      );
    },
    [navigate]
  );

  const goToPayments = useCallback(
    ({clearFilters = true, ...params}: PaymentsParams) => {
      navigate(
        getRouteUrl({
          module: 'invoicing',
          view: 'payments',
          clearFilters,
          ...params,
        })
      );
    },
    [navigate]
  );

  const goToClientMoney = useCallback(
    ({clearFilters = true, ...params}: ClientMoneyParams) => {
      navigate(
        getRouteUrl({
          module: 'invoicing',
          view: 'client-money',
          clearFilters,
          ...params,
        })
      );
    },
    [navigate]
  );

  const goToFinanceExport = useCallback(
    ({clearFilters = true, ...params}: FinanceExportParams) => {
      navigate(
        getRouteUrl({
          module: 'invoicing',
          view: 'finance-export',
          clearFilters,
          ...params,
        })
      );
    },
    [navigate]
  );

  const goToSearch = useCallback(
    ({clearFilters = true, ...params}: SearchParams) => {
      navigate(getRouteUrl({module: 'search', clearFilters, ...params}));
    },
    [navigate]
  );

  return {
    goToCasusList,
    goToCasusLogView,
    goToCasusDocuments,
    goToCasusJustitiaSubmission,
    goToCasusEmails,
    goToCasusNotes,
    goToCasusTasks,
    goToCasusFees,
    goToCasusInvoiceContainers,
    goToCasusInvoices,
    goToCasusPayments,
    goToCasusClientMoney,
    goToContacts,
    goToTriage,
    goToAllEmails,
    goToTasks,
    goToInvoiceContainers,
    goToInvoices,
    goToPayments,
    goToClientMoney,
    goToFinanceExport,
    goToSearch,
  };
};
